import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';
import { radius } from '../../theme/radius';

export type ScrollSide = 'left' | 'right';

export interface ScrollArrowProps {
  /** Edge the arrow sits on, and the direction it scrolls. */
  side: ScrollSide;
  /** Whether that edge still has items to reveal. */
  visible: boolean;
  onClick: () => void;
  token: GlobalToken;
  /** Space kept free under the row (e.g. its bottom border), in px. */
  bottomInset?: number;
}

/**
 * Fixed rather than taken from `controlHeightXS`: that token is 22 in the
 * legacy theme but 16 under theme-v2 (which leaves `controlHeight` at AntD's
 * default), so the arrow would shrink in the app while Storybook showed it
 * at the intended size.
 */
const ARROW_SIZE = 22;

const ARROW_ICON_SIZE = 11;

const LABEL_KEY: Record<ScrollSide, string> = {
  left: 'components.tabs.scrollLeft',
  right: 'components.tabs.scrollRight',
};

const ICON: Record<ScrollSide, typeof CaretLeftIcon> = {
  left: CaretLeftIcon,
  right: CaretRightIcon,
};

/**
 * One edge nudge button for a horizontally scrolling row. Stays mounted and
 * cross-fades instead of unmounting, so items appearing or disappearing never
 * shift the row.
 */
export const ScrollArrow = ({
  side,
  visible,
  onClick,
  token,
  bottomInset = 0,
}: ScrollArrowProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const Icon = ICON[side];

  return (
    <StyledScrollArrow
      type="button"
      $side={side}
      $visible={visible}
      $token={token}
      $bottomInset={bottomInset}
      tabIndex={visible ? 0 : -1}
      aria-hidden={!visible}
      aria-label={t(LABEL_KEY[side])}
      onClick={onClick}
    >
      <Icon size={ARROW_ICON_SIZE} weight="bold" />
    </StyledScrollArrow>
  );
};

// ─── Styled components ───────────────────────────────────────────────────────

interface StyledScrollArrowProps {
  $token: GlobalToken;
  $side: ScrollSide;
  $visible: boolean;
  $bottomInset: number;
}

/**
 * Round nudge button sitting over the fade. Kept mounted and cross-faded
 * rather than unmounted, so appearing items never shift the row.
 */
const StyledScrollArrow = styled.button<StyledScrollArrowProps>`
  position: absolute;
  top: 50%;
  ${({ $side }) => $side}: ${({ $token }) => $token.marginXS}px;
  /* Offsets the bottom inset, so the button optically centres on the row. */
  transform: translateY(
    calc(-50% - ${({ $bottomInset }) => $bottomInset}px)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${ARROW_SIZE}px;
  height: ${ARROW_SIZE}px;
  padding: 0;
  border: ${({ $token }) => $token.lineWidth}px solid
    ${({ $token }) => $token.colorBorderSecondary};
  border-radius: ${radius.circle};
  background: ${({ $token }) => $token.colorBgContainer};
  box-shadow: ${({ $token }) => $token.boxShadowTertiary};
  color: ${({ $token }) => $token.colorTextTertiary};
  cursor: pointer;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    opacity ${({ $token }) => $token.motionDurationMid} ease,
    color ${({ $token }) => $token.motionDurationMid} ease;

  &:hover {
    color: ${({ $token }) => $token.colorPrimary};
  }
`;
